import { Divider, InfoIcon } from '@wix/design-system';
import React, { useContext } from 'react';
import { RicosContext, ZIndexContext } from 'ricos-context';
import { PollData_Layout_PollLayout_Type, PollData_Layout_PollLayout_Direction } from 'ricos-schema/next';
import { LTRIcon, RTLIcon } from 'wix-rich-content-plugin-commons';
import { ToggleButton, ThumbnailPicker } from 'wix-rich-content-ui-components';
import { POLLS_IMAGE_TO_QUESTION_TOGGLE, POLL_LAYOUT_LIST, POLL_LAYOUT_GRID, POLLS_IMAGE_TO_ANSWERS_TOGGLE, POLL_DIRECTION_LTR, POLL_DIRECTION_RTL } from '../../dataHooks.js';
import { Section } from './section.js';
import { LayoutListIcon } from '../../assets/icons/LayoutListIcon.js';
import { LayoutGridIcon } from '../../assets/icons/LayoutGridIcon.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const LayoutSettingsSection = ({
  updateRicosDocument,
  componentData
}) => {
  var _a, _b, _c;
  const { t } = useContext(RicosContext);
  const zIndexService = useContext(ZIndexContext);
  const { poll, options } = (_a = componentData.layout) != null ? _a : {};
  const updateSettings = (layout) => {
    updateRicosDocument({ layout: __spreadValues(__spreadValues({}, componentData.layout), layout) });
  };
  const handlePollTypeChange = (type) => {
    var _a2, _b2;
    updateSettings({
      poll: __spreadProps(__spreadValues({}, (_a2 = componentData.layout) == null ? void 0 : _a2.poll), { type }),
      option: __spreadProps(__spreadValues({}, (_b2 = componentData.layout) == null ? void 0 : _b2.options), {
        enableImage: type === PollData_Layout_PollLayout_Type.GRID
      })
    });
    window.dispatchEvent(new Event("resize"));
  };
  const handleDirectionChange = (direction) => {
    var _a2;
    updateSettings({
      poll: __spreadProps(__spreadValues({}, (_a2 = componentData.layout) == null ? void 0 : _a2.poll), { direction })
    });
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Section, { title: t("Poll_PollSettings_Tab_Layout_Section_Question_Header") }, /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      checked: Boolean(poll == null ? void 0 : poll.enableImage),
      title: t("Poll_PollSettings_Tab_Layout_Section_Question_Image"),
      dataHook: POLLS_IMAGE_TO_QUESTION_TOGGLE,
      onChange: () => updateSettings({ poll: __spreadProps(__spreadValues({}, poll), { enableImage: !(poll == null ? void 0 : poll.enableImage) }) })
    }
  )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(Section, { title: t("Poll_PollSettings_Tab_Layout_Section_Answers_Header") }, /* @__PURE__ */ React.createElement(
    ThumbnailPicker,
    {
      dataSource: [
        {
          dataHook: POLL_LAYOUT_LIST,
          value: PollData_Layout_PollLayout_Type.LIST,
          label: t("Poll_PollSettings_Tab_Layout_Section_Answers_Layout_List"),
          renderBackgroundImage: (props) => /* @__PURE__ */ React.createElement(LayoutListIcon, __spreadValues({}, props))
        },
        {
          dataHook: POLL_LAYOUT_GRID,
          value: PollData_Layout_PollLayout_Type.GRID,
          label: t("Poll_PollSettings_Tab_Layout_Section_Answers_Layout_Grid"),
          renderBackgroundImage: (props) => /* @__PURE__ */ React.createElement(LayoutGridIcon, __spreadValues({}, props))
        }
      ],
      selectedValue: (_b = poll == null ? void 0 : poll.type) != null ? _b : PollData_Layout_PollLayout_Type.LIST,
      title: t("Poll_PollSettings_Tab_Layout_Section_Answers_Layout"),
      onChange: handlePollTypeChange
    }
  ), /* @__PURE__ */ React.createElement(
    ToggleButton,
    {
      checked: Boolean(options == null ? void 0 : options.enableImage),
      title: t("Poll_PollSettings_Tab_Layout_Section_Answers_Image"),
      dataHook: POLLS_IMAGE_TO_ANSWERS_TOGGLE,
      onChange: () => updateSettings({ options: __spreadProps(__spreadValues({}, options), { enableImage: !(options == null ? void 0 : options.enableImage) }) })
    }
  )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(
    Section,
    {
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, t("Poll_PollSettings_Tab_Layout_Section_TextDirection_Header"), "\xA0", /* @__PURE__ */ React.createElement(
        InfoIcon,
        {
          content: t("Poll_PollSettings_Tab_Layout_Section_TextDirection_Header_Tooltip"),
          size: "small",
          tooltipProps: {
            appendTo: (el) => {
              var _a2;
              return Boolean((_a2 = el.getAttribute("data-hook")) == null ? void 0 : _a2.includes("ricos-portal"));
            },
            zIndex: zIndexService.getZIndex("TOOLTIP")
          }
        }
      ))
    },
    /* @__PURE__ */ React.createElement(
      ThumbnailPicker,
      {
        dataSource: [
          {
            dataHook: POLL_DIRECTION_LTR,
            value: PollData_Layout_PollLayout_Direction.LTR,
            label: t("Poll_PollSettings_Tab_Layout_Section_TextDirection_LTR"),
            renderBackgroundImage: (props) => /* @__PURE__ */ React.createElement(LTRIcon, __spreadValues({}, props))
          },
          {
            dataHook: POLL_DIRECTION_RTL,
            value: PollData_Layout_PollLayout_Direction.RTL,
            label: t("Poll_PollSettings_Tab_Layout_Section_TextDirection_RTL"),
            renderBackgroundImage: (props) => /* @__PURE__ */ React.createElement(RTLIcon, __spreadValues({}, props))
          }
        ],
        selectedValue: (_c = poll == null ? void 0 : poll.direction) != null ? _c : PollData_Layout_PollLayout_Direction.LTR,
        title: t("Poll_PollSettings_Tab_Layout_Section_TextDirection_Choose"),
        onChange: handleDirectionChange
      }
    )
  ));
};

export { LayoutSettingsSection };
//# sourceMappingURL=layout-settings-section.js.map
